import Snackbar from '@material-ui/core/Snackbar';
import CloseIcon from '@material-ui/icons/Close';
import bind from 'lodash-decorators/bind';
import type { ReactNode } from 'react';
import * as React from 'react';
import { Component, createContext, isValidElement, useCallback } from 'react';
import type { IntlShape, MessageDescriptor } from 'react-intl';
import { defineMessages, injectIntl } from 'react-intl';
import { useFifoQueue } from '../../hooks/use-queue';
import { isObject } from '../../shared-library/object-utils';
import { createTrappedCallable } from '../../utils/dev-utils';
import { formatMessageOrString } from '../../utils/intl';
import { isMessageDescriptor } from '../../utils/react-utils';
import type { TAlertAction } from '../Alert';
import Alert from '../Alert';
import { Text, Title2 } from '../wysiwyg/wysiwyg';
import css from './snackbar.module.scss';

const messages = defineMessages({
  close: { defaultMessage: 'Close notification' },
});

type Props = {
  snackbars: ISnackObj[],
  shiftSnack(): void,
  intl: IntlShape,
};

type State = {
  open: boolean,
};

const TIME_BETWEEN_SNACKS = 500;

type TFuzzyMessage = ReactNode | MessageDescriptor;

export type ISnack = TFuzzyMessage | ISnackObj;

export type TSnackAction = TAlertAction;

export type ISnackObj = {
  content: TFuzzyMessage,
} & IAddSnackOpts;

export type IAddSnackOpts = {
  title?: TFuzzyMessage,
  actions?: TSnackAction[],
  type: 'error' | 'info' | 'warn' | 'success',
};

export function isValidSnack(val: any): val is ISnack {
  const type = typeof val;

  return type === 'string' || type === 'number' || isValidElement(val) || isMessageDescriptor(val) || (isObject(val) && 'content' in val);
}

export interface TAddSnackFn {
  (snack: TFuzzyMessage, opts: IAddSnackOpts): void;
  (opts: ISnackObj): void;
}

export const SnackbarContext = createContext<TAddSnackFn>(createTrappedCallable());

export function SnackbarProvider(props: { children: ReactNode }) {
  const [snackbars, pushSnack, shiftSnack] = useFifoQueue<ISnackObj>();

  const addSnack = useCallback((snack: ISnack, opts?: IAddSnackOpts) => {
    const theSnack = isSnackObj(snack) ? {
      ...snack,
      ...opts,
    } : {
      content: snack,
      ...opts,
    };

    pushSnack(theSnack);
  }, [pushSnack]);

  return (
    <SnackbarContext.Provider value={addSnack}>
      {props.children}
      <AppSnackbar snackbars={snackbars} shiftSnack={shiftSnack} />
    </SnackbarContext.Provider>
  );
}

class AppSnackbarNoIntl extends Component<Props, State> {

  state = {
    open: true,
  };

  _running = false;

  @bind
  handleClose() {
    if (this._running) {
      return;
    }

    this._running = true;

    this.setState({ open: false });

    setTimeout(() => {
      this.props.shiftSnack();

      this.setState({ open: true });

      this._running = false;
    }, TIME_BETWEEN_SNACKS);
  }

  render() {
    const { intl, snackbars } = this.props;

    if (snackbars.length === 0) {
      return null;
    }

    const snack = snackbars[0];

    const title = formatMessageOrString(intl, snack.title);
    const content = formatMessageOrString(intl, snack.content);
    const actions = snack.actions ?? [];
    const type = snack.type;

    return (
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={this.state.open}
        onClose={this.handleClose}
        onClick={this.handleClose}
        ContentProps={{
          'aria-describedby': 'message-id',
        }}
        className={css.snackbar}
      >
        <Alert
          shrink
          type={type}
          onActionClick={this.handleClose}
          actions={[
            ...actions,
            {
              icon: CloseIcon,
              onClick: this.handleClose,
              title: this.props.intl.formatMessage(messages.close),
              key: 'close',
            },
          ]}
        >
          {title && <Title2>{title}</Title2>}
          {typeof content === 'string' ? <Text>{content}</Text> : content}
        </Alert>
      </Snackbar>
    );
  }
}

function isSnackObj(item: any): item is ISnackObj {
  return item != null && item.content !== undefined;
}

export const AppSnackbar = injectIntl(AppSnackbarNoIntl);

export function useSnackbar(): TAddSnackFn {
  return React.useContext(SnackbarContext);
}
